import { Check, CircleAlert, CircleCheckBig, Clock, Copy, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ApiError } from '../../api/envelope';
import type { ShortLinkDomainState } from '../../api/types';
import { useTimeFormat } from '../../lib/tenantTime';
import { useLoad } from '../../lib/useLoad';
import { sinceLabel } from './automationTools';
import { LinkDomainArt } from './campaignArt';
import { linkDomainStatus, linkDomainSteps, recordName, type StepState } from './campaignSettingsTools';
import { useComms } from './context';

/**
 * The company's own link domain — what campaign links look like to the people
 * who get them: `https://go.yourcompany.com/x7Kp9Qa` in a text, and the click
 * links in an email. Carriers and mail filters trust a link on the sender's own
 * domain far more than one on a shared address, and a short one keeps a text
 * to one segment.
 *
 * Laid out as the three things an operator actually does, in order — pick a
 * subdomain, add one record at their DNS provider, check it — because the
 * record is the part done somewhere else, by hand, and is where it goes wrong.
 * Saving checks the DNS at once; links move onto the domain only once it
 * points here. The certificate for it is set up on the server — the
 * deployment's runbook (docs/short-links.md) — which is why a verified domain
 * still says to try a link before relying on it.
 */
export function LinkDomainSection() {
  const { source, capabilities } = useComms();
  const { fmtDateTime } = useTimeFormat();
  const mayChange = capabilities?.access.features.campaigns === 'full';
  const load = useLoad(() => source.api.getShortLinkDomain(), [source]);
  const [state, setState] = useState<ShortLinkDomainState | null>(null);
  const [host, setHost] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const current = state ?? load.data;

  const act = async (action: () => Promise<ShortLinkDomainState>) => {
    setBusy(true);
    setError(null);
    try {
      setState(await action());
      setEditing(false);
      setHost('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(value);
    } catch {
      setCopied(null);
    }
  };

  if (!current) {
    return (
      <div className="cpset-card cpset-pad cpset-muted">
        {load.error ? 'Could not load the link domain.' : 'Loading…'}
      </div>
    );
  }
  const domain = current.domain;
  const steps = linkDomainSteps(current);
  const status = linkDomainStatus(current);
  const showInput = !domain || editing;
  const typed = host.trim();
  // The record follows what is being typed, so the Name an operator is about to
  // copy is the one for the subdomain they are about to save.
  const recordHost = (showInput && typed) || domain?.host || null;
  const record = recordName(recordHost ?? 'go.yourcompany.com');

  return (
    <>
      <section className="cpset-card cpset-hero">
        <LinkDomainArt className="cpset-hero-art" />
        <div className="cpset-hero-text">
          <h2>Put campaign links on your own address</h2>
          <p>
            Texts and tracked emails then link to <code>go.yourcompany.com/x7Kp9Qa</code>. Carriers and mail
            filters trust a link on the sender’s own domain far more than a shared one.
          </p>
          <span className="cpset-hero-now">
            Today links use <code>{current.shortBase.replace(/^https?:\/\//, '')}</code>
          </span>
        </div>
      </section>

      {!current.cnameTarget ? (
        <div className="cmg-insight">
          <CircleAlert size={15} />
          <span>Custom link domains are not set up on this deployment yet — ask your LimoSphere contact.</span>
        </div>
      ) : (
        <section className="cpset-card" aria-label="Set it up">
          <div className="cpset-steps-head">
            <b>Set it up</b> <span>· about 5 minutes, plus however long DNS takes</span>
          </div>

          <div className="cpset-step">
            <StepMark n={1} state={steps[0]} />
            <div className="cpset-step-body">
              <b>Pick a subdomain you are not using</b>
              {showInput && mayChange ? (
                <div className="cpset-inline">
                  <input
                    className="cmg-input cpset-host-input"
                    aria-label="Link domain"
                    value={host}
                    placeholder={domain?.host ?? 'go.yourcompany.com'}
                    onChange={(e) => setHost(e.target.value)}
                  />
                  <button
                    type="button"
                    className="cbtn cbtn-primary"
                    disabled={busy || !typed}
                    onClick={() => void act(() => source.api.saveShortLinkDomain(typed))}
                  >
                    {domain ? 'Save and check' : 'Save'}
                  </button>
                  {editing && (
                    <button type="button" className="cbtn" onClick={() => setEditing(false)}>
                      Cancel
                    </button>
                  )}
                </div>
              ) : domain ? (
                <div className="cpset-inline">
                  <code className="cpset-host">{domain.host}</code>
                  {mayChange && (
                    <button type="button" className="cbtn" onClick={() => setEditing(true)}>
                      Change
                    </button>
                  )}
                </div>
              ) : null}
              {showInput && mayChange && (
                <span className="cpset-hint">
                  A subdomain like <code>go.yourcompany.com</code> or <code>links.yourcompany.com</code> — one that
                  points nowhere today.
                  {domain && ' Changing it takes links off the old one at once.'}
                </span>
              )}
              {!mayChange && <span className="cpset-hint">Changing the link domain needs Campaigns at Send.</span>}
            </div>
          </div>

          <div className="cpset-step">
            <StepMark n={2} state={steps[1]} />
            <div className="cpset-step-body">
              <b>Add this record where your domain is managed</b>
              <span className="cpset-hint">Your registrar or DNS provider. Copy each value exactly.</span>
              <div className="cpset-table-wrap">
                <table className="cpset-table cpset-dns">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Points to</th>
                      <th aria-label="Copy" />
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="mono">
                        <b>CNAME</b>
                      </td>
                      <td className="mono">{record.name}</td>
                      <td className="mono">{current.cnameTarget}</td>
                      <td className="cpset-cell-end">
                        <button
                          type="button"
                          className="cbtn cbtn-sm"
                          aria-label={`Copy ${current.cnameTarget}`}
                          onClick={() => void copy(current.cnameTarget as string)}
                        >
                          {copied === current.cnameTarget ? <Check size={13} /> : <Copy size={13} />}
                          <span className="cpset-copy-label">
                            {copied === current.cnameTarget ? 'Copied' : 'Copy'}
                          </span>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {recordHost && !record.apex && (
                <span className="cpset-hint">
                  Some providers want the whole name instead — <code>{recordHost}</code>.
                </span>
              )}
              {recordHost && record.apex && (
                <span className="cpset-hint cpset-warn-text">
                  {recordHost} is a whole domain, and most providers cannot point one with a CNAME. Use a subdomain
                  of it instead.
                </span>
              )}
            </div>
          </div>

          <div className="cpset-step">
            <StepMark n={3} state={steps[2]} />
            <div className="cpset-step-body">
              <b>Check it</b>
              {status ? (
                <div className={`cpset-status ${status.tone}`} role="status">
                  {status.tone === 'ok' ? (
                    <CircleCheckBig size={16} />
                  ) : status.tone === 'warn' ? (
                    <Clock size={16} />
                  ) : (
                    <CircleAlert size={16} />
                  )}
                  <span className="cpset-status-text">
                    <b>{status.title}</b> — {status.detail}
                  </span>
                  {mayChange && (
                    <button
                      type="button"
                      className="cbtn"
                      disabled={busy}
                      onClick={() => void act(() => source.api.verifyShortLinkDomain())}
                    >
                      <RefreshCw size={13} />
                      Check again
                    </button>
                  )}
                </div>
              ) : (
                <span className="cpset-hint">Saving the domain checks it straight away.</span>
              )}
              {domain?.checkedAt && (
                <span className="cpset-hint">
                  Last checked {sinceLabel(domain.checkedAt, fmtDateTime)}.{' '}
                  {domain.verified
                    ? // Not "a test send": that sends its links as written, so it never
                      // touches this domain. A real campaign to yourself does.
                      'Before relying on it, send a campaign to just yourself and open its link — a test send keeps its links as written.'
                    : 'Links move onto it from the next send once it passes; links already sent keep working.'}
                </span>
              )}
            </div>
          </div>
        </section>
      )}

      {error && <div className="cmg-insight bad">{error}</div>}

      {domain && mayChange && (
        <section className="cpset-card cpset-pad cpset-remove">
          <div className="cpset-remove-text">
            <b>Remove the link domain</b>
            <span className="cpset-hint">
              Links already sent on it stop working at once. Remove it only when the campaigns that used it are old,
              and the DNS record after that.
            </span>
          </div>
          <button type="button" className="cbtn cpset-danger" disabled={busy} onClick={() => setConfirmRemove(true)}>
            Remove
          </button>
        </section>
      )}

      {confirmRemove && domain && (
        <ConfirmDialog
          title={`Remove ${domain.host}?`}
          body="Every link already sent on it stops working at once, and new campaign links go back to the shared address."
          confirmLabel="Remove"
          danger
          busy={busy}
          onCancel={() => setConfirmRemove(false)}
          onConfirm={() => {
            setConfirmRemove(false);
            void act(() => source.api.removeShortLinkDomain());
          }}
        />
      )}
    </>
  );
}

/** A step's mark: a tick when done, its number filled when it is the one to do, outlined after. */
function StepMark({ n, state }: { n: number; state: StepState }) {
  return (
    <span className={`cpset-step-mark ${state}`} aria-label={state === 'done' ? `Step ${n}, done` : `Step ${n}`}>
      {state === 'done' ? <Check size={14} strokeWidth={3} /> : n}
    </span>
  );
}
